/**
 * What "Apply" sends for one suggestion reviewed in Learning.
 *
 * Two of the review's controls change what an apply MEANS rather than whether it
 * happens: the name picker on a document-type suggestion (file into an existing
 * type, take the proposed new one, or type another), and the per-document
 * exclusions. Both are held in the review's own state, keyed by suggestion id,
 * and both are arguments to the apply call.
 */

/** The picker's opt-in to free text. Selected, the typed box supplies the name. */
export const CUSTOM_TYPE_NAME = '__custom__';

/** Name presentation only. The original intent still goes to the native owner,
 * which decides canonical type identity with OrdinalIgnoreCase. Do not use a
 * locale collation to invent equivalent Unicode names on the client. */
export const suggestionTypeName = (raw: string, documentTypes: readonly { name: string }[]) => {
  const trimmed = raw.trim();
  const existing =
    documentTypes.find((type) => type.name === trimmed) ??
    (/^[\x00-\x7f]*$/.test(trimmed)
      ? documentTypes.find(
          (type) =>
            /^[\x00-\x7f]*$/.test(type.name) && type.name.toLowerCase() === trimmed.toLowerCase(),
        )
      : undefined);
  return { name: existing?.name ?? trimmed, existing: Boolean(existing) };
};

/** Selection is the suggestion's matching membership, not the number the
 * engine will change: protected/already-filed documents may be skipped. */
export const suggestionGroupSelection = (
  entry: { supportCount: number; evidenceDocumentIds?: readonly string[] | null },
  excluded: readonly string[],
) => {
  const total =
    Number.isSafeInteger(entry.supportCount) && entry.supportCount >= 0 ? entry.supportCount : null;
  const members = new Set(entry.evidenceDocumentIds ?? []);
  const omitted = new Set(excluded.filter((id) => members.has(id))).size;
  const complete =
    total !== null &&
    Array.isArray(entry.evidenceDocumentIds) &&
    entry.evidenceDocumentIds.every((id) => typeof id === 'string' && id.trim().length > 0) &&
    members.size === total;
  return { total, included: complete ? total - omitted : null, excluded: omitted };
};

export type SuggestionDocketChoices = {
  /** The picker's value per suggestion id — a type name, or CUSTOM_TYPE_NAME. */
  names: Record<string, string>;
  /** The typed name per suggestion id, meaningful only when the picker is custom. */
  customNames: Record<string, string>;
  /** Documents the operator unticked in the evidence grid, per suggestion id. */
  excluded: Record<string, string[]>;
};

export type SuggestionApplyIntent = {
  suggestionId: string;
  /** Undefined when the operator chose nothing — the engine keeps its own proposal. */
  proposedName?: string;
  /** Undefined rather than empty, so an untouched card sends no exclusion list. */
  excludedDocumentIds?: string[];
  /** A new-type card: the proposed fields kept (D10). Undefined or empty adds the type with no fields. */
  keptFieldNames?: string[];
  /**
   * The operator opted into typing a name and has not typed one yet. Apply is
   * disabled in this state rather than applying under a blank name.
   */
  blocked: boolean;
};

/** Everything one suggestion's Apply sends, resolved from the review's choices. */
export const suggestionApplyIntent = (
  suggestionId: string,
  choices: SuggestionDocketChoices,
): SuggestionApplyIntent => {
  const picked = choices.names[suggestionId];
  const typed = (choices.customNames[suggestionId] ?? '').trim();
  const custom = picked === CUSTOM_TYPE_NAME;
  const name = custom ? typed : (picked ?? '').trim();
  const excluded = choices.excluded[suggestionId] ?? [];
  return {
    suggestionId,
    proposedName: name.length > 0 ? name : undefined,
    excludedDocumentIds: excluded.length > 0 ? [...excluded] : undefined,
    blocked: custom && typed.length === 0,
  };
};
